import { FC } from 'react'

import { styled } from 'styled-components'

import { authActions } from 'core/auth/authStore'
import {
  Clickable,
  Select,
  FormLabel,
  FieldContainer,
  InputWrapper,
  TextInput,
} from 'core/components'
import DialogComponent, {
  DialogActions,
} from 'core/components/lib/Dialog/Dialog'
import { R } from 'core/helpers'
import { useForm } from 'core/hooks'
import { addEnv, setActiveId } from 'core/stores/env'

import { Fields } from './common'

type FormValues = {
  label: string
  apiBase: string | null
  companyId: string
  userType: string | null
}

type AddEnvDialogProps = {
  isOpen: boolean
  onOpenChange: (openState: boolean) => void
}

const AddEnvDialog: FC<AddEnvDialogProps> = ({ isOpen, onOpenChange }) => {
  const { fields, formState, handleSubmit } = useForm<FormValues>(
    {
      label: '',
      apiBase: null,
      companyId: '',
      userType: 'agent',
    },
    {
      onSubmit: ({ label, apiBase, companyId, userType }) => {
        if (!apiBase || !userType) {
          console.error('API Base and User Type are required.')
          return
        }
        const newEnv = addEnv({
          label,
          apiBase,
          companyId,
          userType,
        })
        if (newEnv?.id) {
          setActiveId(newEnv.id)
          onOpenChange(false)
          authActions.setLoginModalExplicitlyRequested(true)
          window.location.reload()
        }
      },
      rules: {
        label: (value) => (R.isEmpty(value) ? 'Required' : true),
        apiBase: (value) => (R.isNil(value) ? 'Required' : true),
        companyId: (value) => (R.isEmpty(value) ? 'Required' : true),
        userType: (value) => (R.isNil(value) ? 'Required' : true),
      },

      validationType: 'onBlur',
    },
  )

  return (
    <DialogComponent
      content={
        <form>
          <Fields>
            <FieldContainer direction='column'>
              <FormLabel text='Label' />
              <InputWrapper>
                <TextInput
                  placeholder='Enter label'
                  variant='outline'
                  {...fields.label}
                />
              </InputWrapper>
            </FieldContainer>

            <FieldContainer direction='column'>
              <FormLabel text='API Base URL' />
              <Select
                isSearchable={false}
                options={[
                  {
                    label: 'Production',
                    value: import.meta.env.VITE_PRODUCTION_API_BASE,
                    sublabel: (
                      <StyledSublabel>
                        {import.meta.env.VITE_PRODUCTION_API_BASE}
                      </StyledSublabel>
                    ),
                  },
                  {
                    label: 'Sandbox',
                    value: import.meta.env.VITE_SANDBOX_API_BASE,
                    sublabel: (
                      <StyledSublabel>
                        {import.meta.env.VITE_SANDBOX_API_BASE}
                      </StyledSublabel>
                    ),
                  },
                  {
                    label: 'Development',
                    value: import.meta.env.VITE_DEVELOPMENT_API_BASE,
                    sublabel: (
                      <StyledSublabel>
                        {import.meta.env.VITE_DEVELOPMENT_API_BASE}
                      </StyledSublabel>
                    ),
                  },
                ]}
                placeholder='Select API Base URL'
                {...fields.apiBase}
              />
            </FieldContainer>

            <FieldContainer direction='column'>
              <FormLabel text='Company ID' />
              <InputWrapper>
                <TextInput
                  placeholder='Enter Company ID'
                  variant='outline'
                  {...fields.companyId}
                />
              </InputWrapper>
            </FieldContainer>

            <FieldContainer direction='column'>
              <FormLabel text='User Type' />
              <Select
                isSearchable={false}
                options={[
                  { label: 'Agent', value: 'agent' },
                  { label: 'Borrower', value: 'borrower' },
                ]}
                placeholder='Select User Type'
                {...fields.userType}
              />
            </FieldContainer>
          </Fields>
          <DialogActions>
            <Clickable onClick={() => onOpenChange(false)} variant='secondary'>
              Close
            </Clickable>
            <Clickable
              disabled={!formState.isValid || !formState.isDirty}
              onClick={handleSubmit}
              variant='primary'
            >
              Add Environment
            </Clickable>
          </DialogActions>
        </form>
      }
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title='Add Environment'
      width='smallMedium'
    />
  )
}

export default AddEnvDialog

const StyledSublabel = styled.div`
  text-transform: lowercase;
`
